// The layer of the sea objects (R2657, ui-map-interface.md §6.1.14,
// v2-the-deep.md §11.8, §11.9): the slot `seaobjects`, right after `sites`.
//
// It draws what the wire sends and reads no flag (§14.1 rule 5). While the
// flag `sapper` is off the wire carries no row, and the layer draws
// nothing. A row is known or heard (R2654, R2894, R2902):
//
// - a heard row, a sea mine that a hull at sea of the viewer alone hears,
//   draws the generic mine mark: a ring with short spikes and no kind
//   detail, in the ink of its side and dimmer than a known mark
//   (`generic_mine_*` of `[presentation.seaobjects]`). It is one atlas
//   cell in the quad batch of the sea mines, and it never draws a target
//   bracket, because a heard row is never a target;
// - a known sea mine draws the mark of the `seaMine` field in its state,
//   armed or not, and in the ink of its side. A look that holds still is
//   an atlas cell (`render/seaobject-cells.ts`), all of them in ONE
//   instanced quad batch (§10). A look that moves paints on the sheet;
// - a deployable draws the mark of the `deployable` field at its
//   hull share, a line across the heading of the Sapper at the lay. An own
//   or a squad mate's Hark draws its reach at the sonar of the
//   catalog (`SeaObjectSpec.sonar_px`), and a Fanal of the viewer's
//   faction, in a look the player picks, the legs of the shipping lanes of
//   the faction in its reach. The AUTO look draws the Fanal of round
//   139 card 20, keyed on the chart reading (R3072): it draws no reach,
//   because chart card 16 lights the road, and its anchor ring paints again
//   once a step of the anchor time;
// - the relay point of each lit stretch of a lane (R3072, chart card 16):
//   one glow quad and one disc a stretch, packed each frame while the feed
//   hands stretches and the motion is on. It is the one mark of the lane
//   that moves, so the chart packs the road once and this layer the point;
// - a row that is a target of a gun of the viewer (`SeaObjectRow.target`)
//   draws the target bracket: the four corners of the selection bracket of
//   a fleet, in red and fainter (`targetBracket`, R2894). The corners are
//   segments of one batch. The `fleets` layer draws after this one, so the
//   selection bracket stands over a target bracket;
// - the AUTO look `overprint-restricted` draws the area of a field round
//   the armed sea mines of each side in the historical and the modern
//   reading (round 069 cards 12 and 14).
//
// The guide of the chain (R3066, `laneguide.ts`): an own Sapper that
// carries a Fanal near a Fanal of the faction shows where its LAY
// snaps. The reach discs and the snap ring are circles of one batch under
// the sheet, the ghost lights paint on the sheet, and the line from the
// Sapper is a dashed segment over it. The keeper walks the chain again
// only on an edge, and the sheet paints again only when a ghost moved.
//
// The deployables, the areas, the ghosts and the moving sea mines paint on
// ONE paint sheet (`SheetKeeper`), which paints again only when
// the picture changed. A row the `sapperwork` layer draws now (a lay or a
// collect) is hidden here, so no object draws twice.

import { F_ROUND, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import { type BatchSource, type CircleSink, type QuadSink, type SegmentSink, type SheetSink, type SpriteEntry, sheetOf } from './batches';
import { SheetKeeper, clearBox, emptyBox, growBox, type Box } from './sheetpaint';
import { cornerBrackets } from './shared';
import { SeaObjectKind } from '../../gen/proto/aquaphobia';
import { CHART, SEA_OBJECTS } from '../../gen/presentation';
import { seamarks as WORDS } from '../../gen/strings';
import { factionColor } from '../../world';
import { CHART_FELL_FAMILY, CHART_MONO_FAMILY } from '../gfx/atlas';
import { isSeaMineKind, seaKindFigures, type SeaObjectRow } from '../../seaobjects';
import {
  MOVING_SEA_MINE_STYLES, SEA_MINE_KIND_OF, drawsSeaMineArea, paintSeaMine, paintSeaMineArea,
  seaMineExtent, type SeaChartReading, type SeaMineKind, type SeaMineStyle,
} from '../../render/seamine-marks';
import {
  DEPLOYABLE_KIND_OF, LINE_KINDS, MOVING_DEPLOYABLE_STYLES, deployableCards,
  type DeployableKind, type DeployableStyle,
} from '../../render/deployable-marks';
import { paintGhostLight, type LaneLightText, type LightSide } from '../../render/lane-light-looks';
import { pointAt, relayAt, type LaneLine } from '../../lanelit';
import { GLOW_CELL_R, GLOW_KEY } from '../../render/fairway-cells';
import { LaneGuideKeeper, insideSnap, type ChainFigures, type GuideSapper, type LaneGuide } from './laneguide';
import type { ChainPt, ChainTrack } from '../../lanechain';
import { DEPLOYABLE_STYLES } from '../../gen/balance';
import { genericMineCellKey, seaMineCellKey } from '../../render/seaobject-cells';
import { SEA_INK, hh, type Pt } from '../../render/seaobject-kit';
import { AUTO_SCENE_LOOK, type SceneLook } from '../../render/scene-look';
import { reducedMotion } from '../../render/motion';

/** One leg of an own shipping lane, world px. */
export interface SeaLaneLeg { x0: number; y0: number; x1: number; y1: number }

/** Where the layer reads the world. */
export interface SeaObjectFeeds {
  /** The rows of the picture, in id order. */
  rows(): readonly SeaObjectRow[];
  /** A counter that moves on every change of a row. */
  rev(): number;
  /** The viewer's faction. */
  viewer(): number;
  /** The listening range of a kind from the catalog, px; 0 for none. */
  sonarPx(kind: SeaObjectKind): number;
  /** The legs of the shipping lanes of the viewer's faction. */
  laneLegs(): readonly SeaLaneLeg[];
  /** The ids the `sapperwork` layer draws now. */
  hidden(): ReadonlySet<number>;
  /** The share of the anchor time of a deployable that passed, 0 to 1
   *  (R2723); 1 where the page states none. */
  anchorFrac?(r: SeaObjectRow): number;
  /** The guide of the chain (R3066); left out, the layer draws none. */
  chain?: ChainFeeds;
  /** The lit stretches of the lanes whose relay point runs (R3072): the
   *  chart hands them while it shows card 16 in the modern reading. */
  relays?(): readonly RelayFeed[];
}

/** One lit stretch of a lane, as the relay point reads it: its line, the
 *  phase that sets it apart and the alpha of the lane. */
export interface RelayFeed { line: LaneLine; phase: number; mul: number }

/** Where the guide of the chain reads the world (R3066). */
export interface ChainFeeds {
  /** The tracks of the shipping lanes of the viewer's faction: a new
   *  array only when the lanes change. */
  tracks(): readonly ChainTrack[];
  /** The own Sappers, with their stow and their point of the last frame. */
  sappers(): readonly GuideSapper[];
  /** The selected fleet, 0 for none. */
  selected(): number;
  /** The rule figures of the catalog, null while the world sends none. */
  figures(): ChainFigures | null;
  /** The drawn point of a hull this frame, where the line starts. */
  pose?(id: number): { x: number; y: number } | null;
}

/** The AUTO look of the `deployable` field, whose Fanal is the set of
 *  round 115 (R2723). */
const AUTO_DEPLOYABLE: DeployableStyle = DEPLOYABLE_STYLES[0];

/** Which side a deployable row is to the viewer. */
export function lightSide(r: SeaObjectRow, viewer: number): LightSide {
  return r.own ? 'own' : r.faction === viewer ? 'mate' : 'foe';
}

/** The words of the light character of the Fanal. */
const LIGHT_WORDS: LaneLightText = { modern: WORDS.light_char, historical: WORDS.light_char_historical, fell: CHART_FELL_FAMILY };

/** The deployable painters of every look, with the words of the locale. */
const DEPLOYABLE_CARDS = deployableCards({
  radius: (r) => WORDS.radius({ r: Math.round(r) }),
  face: CHART_MONO_FAMILY,
  light: LIGHT_WORDS,
});

/** The figures of the guide of the chain (R3066). */
const GUIDE = CHART.pieces.shipping_lanes.fig;

/** The figures of the relay point (R3072). */
const RELAY = CHART.pieces.shipping_lanes.modern.fig;

/** The half extent of the paint of a ghost light, world px. */
const GHOST_HALF = 16;

const NO_GUIDES: readonly LaneGuide[] = [];

/** The figures of a deployable kind the painters draw to scale. */
function deployableFigures(kind: SeaObjectKind): { L: number; R: number; laneReach: number } {
  const f = seaKindFigures(kind);
  return { L: f?.lengthPx ?? 0, R: f?.lengthPx ?? 0, laneReach: f?.reachPx ?? 0 };
}

/** The half extent of the paint of a deployable, world px. */
export function deployableHalf(kind: DeployableKind, L: number, reach: number, laneReach: number): number {
  const pad = 12;
  if (LINE_KINDS.has(kind)) return L / 2 + pad;
  if (kind === 'cheval') return L + pad;
  if (kind === 'picket') return Math.max(reach, pad) + pad;
  if (kind === 'lane') return Math.max(laneReach, pad) + pad;
  return pad;
}

/** The unit legs of the lanes inside `reach` of a point: both ways along
 *  each leg that passes it. */
export function legsAt(x: number, y: number, reach: number, legs: readonly SeaLaneLeg[]): Pt[] | null {
  const out: Pt[] = [];
  for (const l of legs) {
    const dx = l.x1 - l.x0, dy = l.y1 - l.y0, L2 = dx * dx + dy * dy;
    if (!(L2 > 0)) continue;
    const u = Math.max(0, Math.min(1, ((x - l.x0) * dx + (y - l.y0) * dy) / L2));
    if (Math.hypot(l.x0 + dx * u - x, l.y0 + dy * u - y) > reach) continue;
    const L = Math.sqrt(L2);
    out.push([dx / L, dy / L], [-dx / L, -dy / L]);
  }
  return out.length > 0 ? out : null;
}

/** What a mark painter of the Sapper part is told besides the object. */
export interface MarkPaint {
  /** The map zoom: a hairline keeps its screen width. */
  z: number;
  /** The clock in seconds, and false where the player turned motion off. */
  t: number;
  motion: boolean;
  reading: SeaChartReading;
}

/**
 * Paints the mark of the `seaMine` field at `x, y` at alpha `a` and scale
 * `s`: the one painter of a sea mine on the sheet, in a lay look and in a
 * collect look (R2657).
 */
export function paintSeaMineMark(
  c: CanvasRenderingContext2D, style: SeaMineStyle, kind: SeaObjectKind, x: number, y: number,
  col: number, armed: boolean, hostile: boolean, a: number, s: number, ph: number, o: MarkPaint,
): void {
  const k = SEA_MINE_KIND_OF.get(kind), f = seaKindFigures(kind);
  if (!k || !f || !(a > 0)) return;
  c.save(); c.translate(x, y); if (s !== 1) c.scale(s, s); c.globalAlpha *= a;
  paintSeaMine(c, style, k, {
    col, armed, hostile, t: o.t, ph, s: o.z * s, da: 0, motion: o.motion,
    contact: f.triggerPx, splash: f.splashPx, reading: o.reading,
  });
  c.restore();
}

/** What the Fanal of the AUTO look reads of its row (R2723). */
export interface LightState { side: LightSide; armed: boolean; anchor: number }

/**
 * Paints the mark of the `deployable` field at the origin of `c`, a line
 * along +x, at alpha `a` (R2657). `rot` is the turn of a line kind on the
 * map; `reach` the listening range of a Hark; `light` the side and
 * the anchor of a Fanal, an own acting one where it is left out.
 */
export function paintDeployableMark(
  c: CanvasRenderingContext2D, style: DeployableStyle, kind: SeaObjectKind, col: number, hp: number,
  a: number, rot: number, reach: number, legs: readonly Pt[] | null, o: MarkPaint, light?: LightState,
): void {
  const k = DEPLOYABLE_KIND_OF.get(kind);
  if (!k || !(a > 0)) return;
  const fig = deployableFigures(kind);
  c.save(); c.globalAlpha *= a;
  DEPLOYABLE_CARDS[style][k](c, {
    col, hp: Math.max(0, Math.min(1, hp)), t: o.t, motion: o.motion,
    L: fig.L, R: fig.R, reach, laneReach: fig.laneReach, legs, rot,
    side: light?.side, armed: light?.armed, anchor: light?.anchor, z: o.z, historical: o.reading === 'historical',
  });
  c.restore();
}

/**
 * Packs the target bracket round (x, y) into `q` (R2894): the corners of
 * the selection bracket of a fleet, two arms a corner, in the red and at
 * the alpha of `[presentation.seaobjects]`.
 */
export function targetBracket(q: Pick<SegmentSink, 'line'>, x: number, y: number): void {
  const o = SEA_OBJECTS;
  cornerBrackets(q, x, y, o.targetBracketR, o.targetBracketArm, o.targetBracketWidth,
    o.targetBracketColor, o.targetBracketAlpha, F_ROUND);
}

/** The figures of the generic mine mark (R2902). */
const GENERIC_MINE = {
  r: SEA_OBJECTS.genericMineR, spike: SEA_OBJECTS.genericMineSpike,
  spikes: SEA_OBJECTS.genericMineSpikes, width: SEA_OBJECTS.genericMineWidth,
} as const;

/** The atlas key of the generic mine mark (R2902). */
export const GENERIC_MINE_KEY = genericMineCellKey(GENERIC_MINE);

/**
 * One thing the sheet paints: a moving sea mine (`r`, `style`), a
 * deployable (`r`, `kind`), the area of a side (`pts`, `own`, `col`) or the
 * ghost light of a chain point (`x`, `y`, `col`).
 * The layer keeps a pool of items and fills them anew each frame, so a
 * frame makes no object per item.
 */
interface Item {
  t: 'mine' | 'dep' | 'area' | 'ghost';
  r: SeaObjectRow | null;
  x: number;
  y: number;
  style: SeaMineStyle;
  kind: DeployableKind;
  pts: Pt[];
  own: boolean;
  col: number;
}

const NO_PTS: Pt[] = [];

/** The parts of the sheet key; the key string is built only when one of
 *  them changes. */
interface KeyParts {
  rev: number; mine: string; dep: string; reading: string; viewer: number; hidden: number; anchor: number; guide: number;
}

export class SeaObjectLayer implements Layer {
  readonly name = 'seaobjects';
  private shown = true;
  private readonly quads: QuadSink;
  /** The target brackets (R2894). */
  private readonly brackets: SegmentSink;
  /** The guide of the chain (R3066): the discs and the snap ring under the
   *  sheet, the line over it, and the keeper of the walk. */
  private readonly guideCircles: CircleSink;
  private readonly guideLines: SegmentSink;
  /** The relay points of the lit stretches (R3072): a glow and a dot. */
  private readonly relayGlow: QuadSink;
  private readonly relayDots: CircleSink;
  readonly guideKeeper = new LaneGuideKeeper();
  private readonly sheet: SheetSink;
  private readonly keeper: SheetKeeper;
  private look: SceneLook = AUTO_SCENE_LOOK;
  private reading: SeaChartReading = 'off';
  private motion = !reducedMotion();
  private readonly items: Item[] = [];
  /** The items of the pool; `items` holds the first ones a frame uses. */
  private readonly itemPool: Item[] = [];
  private readonly areas = new Map<number, Pt[]>();
  /** The point lists and the points of the areas, kept from frame to frame. */
  private readonly ptsPool: Pt[][] = [];
  private readonly ptPool: [number, number][] = [];
  private nPts = 0;
  private nPt = 0;
  private sheetKey = '';
  private readonly keyParts: KeyParts = {
    rev: NaN, mine: '', dep: '', reading: '', viewer: NaN, hidden: NaN, anchor: NaN, guide: NaN,
  };
  /** The viewer the sheet paints for, and the paint bound once, so a frame
   *  makes no closure. */
  private paintViewer = 0;
  private readonly paintSheet = (c: CanvasRenderingContext2D, z: number): void => this.paint(c, z, this.paintViewer);
  private readonly box: Box = emptyBox();
  private timeS = 0;

  constructor(private readonly src: BatchSource, private readonly feeds: SeaObjectFeeds) {
    this.quads = src.newQuads(64);
    this.brackets = src.newSegments(64);
    this.guideCircles = src.newCircles(16);
    this.guideLines = src.newSegments(4);
    this.relayGlow = src.newQuads(8);
    this.relayDots = src.newCircles(8);
    this.sheet = sheetOf(src);
    this.keeper = new SheetKeeper(this.sheet);
  }

  /** The `seaMine` and `deployable` fields of the look (R2652). */
  setLook(l: SceneLook): void {
    if (l.seaMine === this.look.seaMine && l.deployable === this.look.deployable) { this.look = l; return; }
    this.look = l;
    this.motion = !reducedMotion();
    this.keeper.invalidate();
  }

  /** The chart reading the AUTO look follows (the CHART setting). */
  setReading(r: SeaChartReading): void { this.reading = r; }

  /** How many sea mines drew from the atlas on the last frame. */
  get sprites(): number { return this.quads.count; }

  /** How many target bracket segments packed on the last frame. */
  get bracketSegments(): number { return this.brackets.count; }

  /** How many circles and lines of the guide packed on the last frame. */
  get guideCircleCount(): number { return this.guideCircles.count; }
  get guideLineCount(): number { return this.guideLines.count; }

  /** How many relay points packed on the last frame. */
  get relayCount(): number { return this.relayDots.count; }

  /** How many times the sheet painted. The tests read it. */
  get paints(): number { return this.keeper.paints; }

  update(frame: FrameInfo): void {
    this.timeS = frame.timeS;
    const quads = this.quads, items = this.items, box = this.box, areas = this.areas;
    quads.begin();
    this.brackets.begin();
    items.length = 0;
    areas.clear();
    this.nPts = 0;
    this.nPt = 0;
    clearBox(box);
    const viewer = this.feeds.viewer(), hidden = this.feeds.hidden();
    const mineStyle = this.look.seaMine as SeaMineStyle, depStyle = this.look.deployable as DeployableStyle;
    const movingMine = this.motion && MOVING_SEA_MINE_STYLES.has(mineStyle);
    const area = drawsSeaMineArea(mineStyle, this.reading);
    const c = frame.cull;
    let moving = false;
    // The anchor steps of the Fanals of the AUTO look: the sheet paints
    // again when one moves a step, and not once a frame (R2723).
    const auto = depStyle === AUTO_DEPLOYABLE;
    const steps = Math.max(1, Math.round(CHART.pieces.shipping_lanes.fig.anchor_steps));
    let anchorKey = 0;
    for (const r of this.feeds.rows()) {
      if (hidden.has(r.id)) continue;
      if (r.heard) {
        // The generic mine mark of a heard row (R2902): no kind, no target bracket.
        if (r.x < c.x0 || r.x > c.x1 || r.y < c.y0 || r.y > c.y1) continue;
        quads.sprite(r.x, r.y, 0, this.src.sprite(GENERIC_MINE_KEY), factionColor(r.faction), SEA_OBJECTS.genericMineAlpha);
        continue;
      }
      const f = seaKindFigures(r.kind);
      if (f && r.target && !r.own && r.x >= c.x0 && r.x <= c.x1 && r.y >= c.y0 && r.y <= c.y1) {
        targetBracket(this.brackets, r.x, r.y);
      }
      if (!f) continue;
      if (isSeaMineKind(r.kind)) {
        const kind = SEA_MINE_KIND_OF.get(r.kind)!;
        if (area && r.armed) {
          let pts = areas.get(r.faction);
          if (!pts) { pts = this.ptsOf(); areas.set(r.faction, pts); }
          pts.push(this.pt(r.x, r.y));
        }
        if (movingMine) {
          this.item('mine', r).style = mineStyle;
          growBox(box, r.x, r.y, seaMineExtent(mineStyle, kind, f.triggerPx, f.splashPx));
          moving = true;
          continue;
        }
        if (r.x < c.x0 || r.x > c.x1 || r.y < c.y0 || r.y > c.y1) continue;
        const e = this.cell(mineStyle, kind, r, viewer, f.triggerPx, f.splashPx);
        quads.sprite(r.x, r.y, 0, e, factionColor(r.faction), 1);
        continue;
      }
      const kind = DEPLOYABLE_KIND_OF.get(r.kind);
      if (!kind) continue;
      this.item('dep', r).kind = kind;
      growBox(box, r.x, r.y, deployableHalf(kind, f.lengthPx ?? 0, this.feeds.sonarPx(r.kind), f.reachPx ?? 0));
      if (this.motion && MOVING_DEPLOYABLE_STYLES.has(depStyle)) moving = true;
      if (auto && kind === 'lane' && !r.armed) anchorKey = (anchorKey * 31 + r.id * 97 + this.anchorStep(r, steps)) | 0;
    }
    for (const [faction, pts] of areas) {
      if (pts.length < 2) continue;
      const it = this.item('area', null);
      it.pts = pts; it.own = faction === viewer; it.col = factionColor(faction);
      for (const p of pts) growBox(box, p[0], p[1], 40);
    }
    for (const g of this.packGuides(viewer)) {
      for (const p of g.ghosts) {
        const it = this.item('ghost', null);
        it.x = p.x; it.y = p.y; it.col = factionColor(viewer);
        growBox(box, p.x, p.y, GHOST_HALF);
      }
    }
    this.packRelays(frame.timeS);
    const key = this.keyOf(this.feeds.rev(), mineStyle, depStyle, viewer, hidden.size, anchorKey, this.guideKeeper.rev);
    this.paintViewer = viewer;
    this.keeper.frame(items.length > 0 ? box : null, frame.cull, frame.view, frame.cam, key, moving, this.paintSheet);
  }

  /** The next item of the frame, from the pool, with its kind and row. */
  private item(t: Item['t'], r: SeaObjectRow | null): Item {
    const n = this.items.length;
    let it = this.itemPool[n];
    if (!it) { it = { t, r, x: 0, y: 0, style: '' as SeaMineStyle, kind: 'picket', pts: NO_PTS, own: false, col: 0 }; this.itemPool[n] = it; }
    it.t = t; it.r = r; it.pts = NO_PTS;
    this.items.push(it);
    return it;
  }

  /** An empty point list of an area, from the pool. */
  private ptsOf(): Pt[] {
    let pts = this.ptsPool[this.nPts];
    if (!pts) { pts = []; this.ptsPool[this.nPts] = pts; }
    this.nPts++;
    pts.length = 0;
    return pts;
  }

  /** A point of an area, from the pool. */
  private pt(x: number, y: number): Pt {
    let q = this.ptPool[this.nPt];
    if (!q) { q = [x, y]; this.ptPool[this.nPt] = q; } else { q[0] = x; q[1] = y; }
    this.nPt++;
    return q;
  }

  /** The sheet key: the same string as long as its parts stay. */
  private keyOf(rev: number, mine: string, dep: string, viewer: number, hidden: number, anchor: number,
    guide: number): string {
    const k = this.keyParts;
    if (k.rev !== rev || k.mine !== mine || k.dep !== dep || k.reading !== this.reading || k.viewer !== viewer
      || k.hidden !== hidden || k.anchor !== anchor || k.guide !== guide) {
      k.rev = rev; k.mine = mine; k.dep = dep; k.reading = this.reading; k.viewer = viewer; k.hidden = hidden; k.anchor = anchor;
      k.guide = guide;
      this.sheetKey = `${rev}|${mine}|${dep}|${this.reading}|${viewer}|${hidden}|${anchor}|${guide}`;
    }
    return this.sheetKey;
  }

  /** The Fanals of the viewer's faction in the picture, in id order:
   *  the set the server reads for the snap (R3066). */
  private laneLights(viewer: number): ChainPt[] {
    return this.feeds.rows().filter((r) => !r.heard && r.kind === SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT && r.faction === viewer);
  }

  /**
   * Packs the guides of the chain of this frame (R3066): the two reach
   * discs, the snap ring, lit while the Sapper stands inside it, and the
   * dashed line from the drawn hull to the chain point. It answers the
   * guides, whose ghosts the sheet paints.
   */
  private packGuides(viewer: number): readonly LaneGuide[] {
    this.guideCircles.begin();
    this.guideLines.begin();
    const ch = this.feeds.chain;
    if (!ch) return NO_GUIDES;
    const fig = ch.figures();
    const sappers = ch.sappers();
    const guides = this.guideKeeper.get({
      sappers, lights: () => this.laneLights(viewer), rev: this.feeds.rev(), tracks: ch.tracks(), fig,
      selected: ch.selected(), guidePx: GUIDE.guide_px, max: Math.round(GUIDE.guide_max), step: GUIDE.guide_step_px,
    });
    if (!fig) return guides;
    const col = factionColor(viewer), q = this.guideCircles;
    for (const g of guides) {
      q.disc(g.lx, g.ly, fig.reach, col, GUIDE.guide_disc_alpha);
      q.disc(g.cx, g.cy, fig.reach, col, GUIDE.guide_disc_alpha);
      q.ring(g.lx, g.ly, fig.reach, GUIDE.guide_rim_width, col, GUIDE.guide_rim_alpha);
      q.ring(g.cx, g.cy, fig.reach, GUIDE.guide_rim_width, col, GUIDE.guide_rim_alpha);
      const s = sappers.find((x) => x.id === g.sapper);
      const lit = s !== undefined && insideSnap(g, s.x, s.y, fig.snap);
      q.ring(g.cx, g.cy, fig.snap, lit ? GUIDE.guide_snap_lit_width : GUIDE.guide_snap_width, col,
        lit ? GUIDE.guide_snap_lit_alpha : GUIDE.guide_snap_alpha);
      const p = ch.pose?.(g.sapper) ?? s;
      if (p) {
        this.guideLines.line(p.x, p.y, g.cx, g.cy, GUIDE.guide_line_width, col, GUIDE.guide_line_alpha, F_ROUND,
          GUIDE.guide_dash_on, GUIDE.guide_dash_off);
      }
    }
    return guides;
  }

  /**
   * Packs the relay point of each lit stretch the feed hands (R3072, card
   * 20): one point runs along the stretch, light to light, and waits
   * between two runs. With the motion off it does not draw.
   */
  private packRelays(t: number): void {
    this.relayGlow.begin();
    this.relayDots.begin();
    const runs = this.feeds.relays?.();
    if (!runs || runs.length === 0 || !this.motion) return;
    const glow = this.src.sprite(GLOW_KEY), white = SEA_INK.white, k = RELAY.relay_glow_px / GLOW_CELL_R;
    for (const r of runs) {
      const d = relayAt(r.line.len, r.phase, t, RELAY.relay_speed_px, RELAY.relay_gap_px);
      if (d === null) continue;
      const p = pointAt(r.line, d);
      this.relayGlow.sprite(p.x, p.y, 0, glow, white, RELAY.relay_glow_alpha * r.mul, k);
      this.relayDots.disc(p.x, p.y, RELAY.relay_dot_px, white, RELAY.relay_dot_alpha * r.mul);
    }
  }

  /** The step of the anchor time of a deployable that passed, 0 to `steps`. */
  private anchorStep(r: SeaObjectRow, steps: number): number {
    return Math.floor(Math.max(0, Math.min(1, this.feeds.anchorFrac?.(r) ?? 1)) * steps);
  }

  /** The atlas cell of one still sea mine. */
  private cell(style: SeaMineStyle, kind: SeaMineKind, r: SeaObjectRow, viewer: number,
    contact: number, splash: number): SpriteEntry {
    return this.src.sprite(seaMineCellKey(style, kind, r.armed, r.faction !== viewer, this.reading,
      { contact, splash }));
  }

  /** Paints the items of the frame on the sheet. */
  private paint(c: CanvasRenderingContext2D, z: number, viewer: number): void {
    const face = this.reading === 'historical' ? CHART_FELL_FAMILY : CHART_MONO_FAMILY;
    const legs = this.feeds.laneLegs();
    const steps = Math.max(1, Math.round(CHART.pieces.shipping_lanes.fig.anchor_steps));
    const mp: MarkPaint = { z, t: this.timeS, motion: this.motion, reading: this.reading };
    for (const it of this.items) {
      if (it.t === 'area') {
        paintSeaMineArea(c, this.reading, { pts: it.pts, side: it.own ? 'own' : 'foe', col: it.col, z, face });
        continue;
      }
      if (it.t === 'ghost') {
        c.save(); c.translate(it.x, it.y);
        paintGhostLight(c, it.col, this.reading === 'historical', GUIDE.guide_ghost_alpha, LIGHT_WORDS);
        c.restore();
        continue;
      }
      const r = it.r!, col = factionColor(r.faction);
      if (it.t === 'mine') {
        paintSeaMineMark(c, it.style, r.kind, r.x, r.y, col, r.armed, r.faction !== viewer, 1, 1, hh(r.id, 7), mp);
        continue;
      }
      const fig = deployableFigures(r.kind);
      const rot = LINE_KINDS.has(it.kind) ? r.heading + Math.PI / 2 : 0;
      // A Fanal of the viewer's faction lifts the fleets of the
      // faction on its lanes, so it draws the legs of the faction's lanes.
      const lane = it.kind === 'lane';
      const legsHere = lane && r.faction === viewer ? legsAt(r.x, r.y, fig.laneReach, legs) : null;
      const light: LightState | undefined = lane
        ? { side: lightSide(r, viewer), armed: r.armed, anchor: r.armed ? 1 : this.anchorStep(r, steps) / steps }
        : undefined;
      c.save(); c.translate(r.x, r.y); if (rot) c.rotate(rot);
      paintDeployableMark(c, this.look.deployable as DeployableStyle, r.kind, col, r.hullPct / 100, 1, rot,
        this.feeds.sonarPx(r.kind), legsHere, mp, light);
      c.restore();
    }
  }

  draw(ctx: DrawCtx): void {
    if (!this.shown) return;
    this.guideCircles.flush(ctx.cam);
    this.sheet.flush(ctx.cam);
    this.quads.flush(ctx.cam);
    this.brackets.flush(ctx.cam);
    this.guideLines.flush(ctx.cam);
    this.relayGlow.flush(ctx.cam);
    this.relayDots.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
